import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import { SystemUpdateService } from '@/services/system-update.service';
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  CustomerCredit,
  CreditType,
  CreditStatus,
  CreditReasonCategory,
  CreditAttributionType,
} from "@/data/types";
import { toUiCredit } from "@/mappers/credit.mapper";

// ── DB row shape — matches customer_credits table ─────────────────────────
export interface CustomerCreditRow {
  id: string;
  credit_id: string;
  customer_id: string;
  job_id: string;        // UUID FK → jobs(id)
  job_number: string | null; // human-readable, e.g. "JOB-001"
  issue_id: string | null;
  related_contractor_id: string | null;
  related_crew_name: string | null;
  credit_amount: number;
  credit_type: string;
  reason_category: string;
  attribution_type: string;
  notes: string | null;
  status: string;
  date_offered: string | null;   // DATE
  approved_by: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ── Service ───────────────────────────────────────────────────────────────
export const CreditService = {
  async getAll(): Promise<{ data: CustomerCredit[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("customer_credits")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as CustomerCreditRow[]).map(toUiCredit), error: null };
  },

  async getByJobNumber(jobNumber: string): Promise<{ data: CustomerCredit[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("customer_credits")
      .select("*")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as CustomerCreditRow[]).map(toUiCredit), error: null };
  },

  async getByCustomerId(customerId: string): Promise<{ data: CustomerCredit[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("customer_credits")
      .select("*")
      .eq("customer_id", customerId)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as CustomerCreditRow[]).map(toUiCredit), error: null };
  },

  async create(credit: CustomerCredit): Promise<{ data: CustomerCredit | null; error: unknown }> {
    // credit.jobId is the job_number (e.g. "JOB-001") — resolve to UUID first
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(credit.jobId), credit.jobId)
      .single();

    if (!jobRow) {
      return { data: null, error: new Error(`Job not found: ${credit.jobId}`) };
    }

    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = {
      credit_id: credit.id,
      customer_id: credit.customerId,
      job_id: jobRow.id,           // UUID for FK
      job_number: credit.jobId,    // human-readable for easy lookup
      issue_id: credit.issueId ?? null,
      related_contractor_id: credit.relatedContractorId ?? null,
      related_crew_name: credit.relatedCrewName ?? null,
      credit_amount: credit.creditAmount,
      credit_type: credit.creditType,
      reason_category: credit.reasonCategory,
      attribution_type: credit.attributionType,
      notes: credit.notes || null,
      status: credit.status,
      date_offered: credit.dateOffered ?? null,
      approved_by: credit.approvedBy ?? null,
      created_by: credit.createdBy,
      organization_id: organizationId,
    };

    const { data, error } = await supabase
      .from("customer_credits")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[CreditService.create] error:", JSON.stringify(error));
      return { data: null, error };
    }
    const jobId = jobRow.id as string;
    const jobLabel = credit.jobId; // credit.jobId is already the human-readable job_number
    void SystemUpdateService.post({
      domain:   'accounting',
      event:    'credit_applied',
      body:     `System: Credit Applied to Job — ${jobLabel}`,
      metadata: {
        title:  'Credit Applied',
        detail: `Job ${jobLabel}`,
        action: { label: 'View Financials', path: `/jobs/${jobId}?tab=financials` },
      },
    });
    return { data: toUiCredit(data as CustomerCreditRow), error: null };
  },

  async update(creditId: string, updates: Partial<CustomerCredit>): Promise<{ data: CustomerCredit | null; error: unknown }> {
    const patch: Record<string, unknown> = {};
    if (updates.creditAmount !== undefined) patch.credit_amount = updates.creditAmount;
    if (updates.creditType !== undefined) patch.credit_type = updates.creditType;
    if (updates.reasonCategory !== undefined) patch.reason_category = updates.reasonCategory;
    if (updates.attributionType !== undefined) patch.attribution_type = updates.attributionType;
    if (updates.notes !== undefined) patch.notes = updates.notes || null;
    if (updates.status !== undefined) patch.status = updates.status;
    if (updates.dateOffered !== undefined) patch.date_offered = updates.dateOffered ?? null;
    if (updates.approvedBy !== undefined) patch.approved_by = updates.approvedBy ?? null;

    const { data, error } = await supabase
      .from("customer_credits")
      .update(patch)
      .eq("credit_id", creditId)
      .select()
      .single();

    if (error || !data) return { data: null, error };
    return { data: toUiCredit(data as CustomerCreditRow), error: null };
  },

  async delete(creditId: string): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("customer_credits")
      .delete()
      .eq("credit_id", creditId);
    return { error };
  },
};
